import { useEffect, useState, useCallback, useRef } from 'react';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../hooks/useAuth';
import axios from '../../lib/axios';
import QRCode from 'react-qr-code';
import JSZip from 'jszip';
import {
  Plus, Pencil, Trash2, PauseCircle, PlayCircle, X, MapPin, Tag, Loader2, Download
} from 'lucide-react';

interface Category {
  id: string;
  name: any; // Json { it: string, en: string }
  color?: string;
  icon?: string;
}

interface Satimap {
  id: string;
  name: string;
}

interface POI {
  id: string;
  name: any; // Json { it: string, en: string }
  description?: any;
  status: string;
  latitude: number;
  longitude: number;
  altitude?: number;
  floor?: number;
  tags: string[];
  categoryId: string;
  satimapId: string;
  qrCode?: string;  // resource URL stored in DB
  qrCodes?: Array<{ id: string; code: string; url: string }>; // QRCode records
  createdAt: string;
  category?: Category;
}

const NEW_CATEGORY_VALUE = '__new__';

/** Returns the SATI gateway URL for a POI's first QRCode record, or null */
function getGatewayUrl(poi: POI): string | null {
  const code = poi.qrCodes?.[0]?.code;
  if (!code) return null;
  const origin = window.location.origin;
  return `${origin}/api/r/${code}?source=qr`;
}

/** Returns gateway URL from map if available, otherwise fallback to localhost */
function getGatewayUrlFromMap(poi: POI, urlMap: Map<string, string>): string | null {
  // Check if URL was fetched from backend
  if (urlMap.has(poi.id)) {
    return urlMap.get(poi.id) || null;
  }
  // Fallback to constructing from code
  return getGatewayUrl(poi);
}

const emptyForm = {
  nameIt: '',
  nameEn: '',
  descriptionIt: '',
  descriptionEn: '',
  categoryId: '',
  satimapId: '',
  latitude: '',
  longitude: '',
  altitude: '',
  floor: '',
  tags: '',
  status: 'active',
  qrCode: '',
};

function getCategoryName(cat?: Category): string {
  if (!cat) return '—';
  if (typeof cat.name === 'string') return cat.name;
  return cat.name?.it || cat.name?.en || '—';
}

// Uses SVG fill attribute (not CSS inline style) to display a dynamic color dot
function ColorDot({ color }: { color: string }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="flex-shrink-0">
      <circle cx="5" cy="5" r="5" fill={color} />
    </svg>
  );
}

export default function PoiManagementView() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenant();
  const token = useAuth().token;

  const [pois, setPois] = useState<POI[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [satimaps, setSatimaps] = useState<Satimap[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPoi, setEditingPoi] = useState<POI | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<POI | null>(null);
  const [deleting, setDeleting] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  
  // Map of POI ID to gateway URL with tenant domain
  const [gatewayUrls, setGatewayUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    setGatewayUrls(new Map());
  }, [selectedTenantId]);

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const fetchAll = useCallback(async () => {
    if (!token || !selectedTenantId) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = `?tenantId=${selectedTenantId}`;
      const [poisRes, catsRes, smapsRes] = await Promise.all([
        axios.get(`/poi${params}`, authHeaders()),
        axios.get(`/poi/categories${params}`, authHeaders()),
        axios.get(`/poi/satimaps${params}`, authHeaders()),
      ]);
      setPois(poisRes.data?.data?.data || poisRes.data?.data || []);
      setCategories(catsRes.data?.data || []);
      setSatimaps(smapsRes.data?.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Errore nel caricamento dati');
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedTenantId, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchGatewayUrl = useCallback(async (poiId: string): Promise<string | null> => {
    if (!token || !selectedTenantId) return null;

    if (gatewayUrls.has(poiId)) {
      return gatewayUrls.get(poiId) || null;
    }

    try {
      const response = await axios.get(
        `/poi/${poiId}/qrcode-url?tenantId=${selectedTenantId}`,
        authHeaders(),
      );
      const gatewayUrl = response.data?.data?.gatewayUrl as string | undefined;

      if (gatewayUrl) {
        setGatewayUrls((prev) => {
          const next = new Map(prev);
          next.set(poiId, gatewayUrl);
          return next;
        });
        return gatewayUrl;
      }

      return null;
    } catch {
      return null;
    }
  }, [token, selectedTenantId, gatewayUrls, authHeaders]);

  const getPoiGatewayUrl = (poi: POI): string | null => {
    if (gatewayUrls.has(poi.id)) {
      return gatewayUrls.get(poi.id) || null;
    }
    // For superadmin never fallback to localhost origin, otherwise QRs would be wrong.
    if (user?.role === 'superadmin') return null;
    return getGatewayUrl(poi);
  };

  useEffect(() => {
    if (!modalOpen || !editingPoi?.id) return;
    void fetchGatewayUrl(editingPoi.id);
  }, [modalOpen, editingPoi?.id, fetchGatewayUrl]);

  function openCreate() {
    setEditingPoi(null);
    setForm(emptyForm);
    setNewCategoryName('');
    setModalOpen(true);
  }

  function openEdit(poi: POI) {
    setEditingPoi(poi);
    setForm({
      nameIt: (poi.name as any)?.it || '',
      nameEn: (poi.name as any)?.en || '',
      descriptionIt: (poi.description as any)?.it || '',
      descriptionEn: (poi.description as any)?.en || '',
      categoryId: poi.categoryId,
      satimapId: poi.satimapId,
      latitude: String(poi.latitude),
      longitude: String(poi.longitude),
      altitude: poi.altitude !== undefined && poi.altitude !== null ? String(poi.altitude) : '',
      floor: poi.floor !== undefined && poi.floor !== null ? String(poi.floor) : '',
      tags: (poi.tags || []).join(', '),
      status: poi.status,
      qrCode: poi.qrCode || '',
    });
    setNewCategoryName('');
    setModalOpen(true);
  }

  async function ensureCategoryId(): Promise<string | null> {
    const typedName = newCategoryName.trim();
    const selected = form.categoryId;

    if (selected && selected !== NEW_CATEGORY_VALUE) {
      return selected;
    }

    if (!typedName) {
      return null;
    }

    const existing = categories.find((c) => {
      const name = getCategoryName(c).trim().toLowerCase();
      return name === typedName.toLowerCase();
    });
    if (existing) {
      return existing.id;
    }

    const response = await axios.post(
      `/poi/categories?tenantId=${selectedTenantId}`,
      { name: typedName },
      authHeaders(),
    );
    const created = response.data?.data as Category | undefined;
    if (!created?.id) {
      return null;
    }

    setCategories((prev) => [...prev, created]);
    return created.id;
  }

  async function handleSave() {
    if (!form.nameIt || !form.satimapId || !form.latitude || !form.longitude) return;
    setSaving(true);
    try {
      const resolvedCategoryId = await ensureCategoryId();
      if (!resolvedCategoryId) {
        alert('Seleziona una categoria o inseriscine una nuova');
        return;
      }

      const payload = {
        ...form,
        categoryId: resolvedCategoryId,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };
      let savedPoiId: string | null = null;
      if (editingPoi) {
        const response = await axios.patch(`/poi/${editingPoi.id}?tenantId=${selectedTenantId}`, payload, authHeaders());
        savedPoiId = response.data?.data?.id || editingPoi.id;
      } else {
        const response = await axios.post(`/poi?tenantId=${selectedTenantId}`, payload, authHeaders());
        savedPoiId = response.data?.data?.id || null;
      }
      if (savedPoiId) {
        await fetchGatewayUrl(savedPoiId);
      }
      setModalOpen(false);
      await fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`/poi/${deleteConfirm.id}?tenantId=${selectedTenantId}`, authHeaders());
      setDeleteConfirm(null);
      await fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || 'Errore nella cancellazione');
    } finally {
      setDeleting(false);
    }
  }

  async function toggleStatus(poi: POI) {
    const newStatus = poi.status === 'active' ? 'suspended' : 'active';
    try {
      await axios.patch(`/poi/${poi.id}/status?tenantId=${selectedTenantId}`, { status: newStatus }, authHeaders());
      await fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || 'Errore nel cambio stato');
    }
  }

  function downloadQRCode() {
    if (qrCodeRef.current) {
      const svg = qrCodeRef.current.querySelector('svg');
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const image = new Image();
        
        image.onload = function () {
          canvas.width = image.width;
          canvas.height = image.height;
          ctx?.drawImage(image, 0, 0);
          const link = document.createElement('a');
          link.href = canvas.toDataURL('image/png');
          // File name reflects what's in the QR
          const gatewayUrl = editingPoi ? getPoiGatewayUrl({ ...editingPoi, qrCodes: editingPoi.qrCodes }) : null;
          const suffix = gatewayUrl ? '-gateway' : '-resource';
          link.download = `qr-${form.nameIt || 'poi'}${suffix}.png`;
          link.click();
        };
        
        image.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      }
    }
  }

  function exportToCSV() {
    if (pois.length === 0) {
      alert('Nessuna attrazione da esportare');
      return;
    }

  // CSV headers — gateway URL is what gets embedded in physical QR, resource URL is the destination
    const headers = ['Nome', 'Categoria', 'Coordinate', 'Tags', 'Gateway QR URL (da stampare)', 'Risorsa (destinazione)', 'QR Immagine'];
    
    // Rows
    const rows = pois.map((poi) => {
      const gatewayUrl = getPoiGatewayUrl(poi);
      return [
        (poi.name as any)?.it || (poi.name as any)?.en || '\u2014',
        getCategoryName(poi.category),
        `${poi.latitude.toFixed(5)}, ${poi.longitude.toFixed(5)}`,
        (poi.tags || []).join('; '),
        gatewayUrl || '\u2014',
        poi.qrCode || '\u2014',
        gatewayUrl
          ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(gatewayUrl)}`
          : '\u2014',
      ];
    });

    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCsv = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `attrazioni-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function exportToZip() {
    if (pois.length === 0) {
      alert('Nessuna attrazione da esportare');
      return;
    }

    const zip = new JSZip();
    const qrFolder = zip.folder('qr-images');

    // Sanitize filename for Windows/all OS
    const sanitizeFilename = (name: string): string => {
      return name
        .replace(/[:<>"/\\|?*]/g, '_')  // Replace invalid chars with underscore
        .replace(/\s+/g, '_')            // Replace spaces with underscores
        .substring(0, 200);              // Limit length
    };

    // CSV content (ZIP version)
    const headers = ['Nome', 'Categoria', 'Coordinate', 'Tags', 'Gateway QR URL', 'Risorsa'];
    const rows = pois.map((poi) => {
      const gatewayUrl = getPoiGatewayUrl(poi);
      return [
        (poi.name as any)?.it || (poi.name as any)?.en || '\u2014',
        getCategoryName(poi.category),
        `${poi.latitude.toFixed(5)}, ${poi.longitude.toFixed(5)}`,
        (poi.tags || []).join('; '),
        gatewayUrl || '\u2014',
        poi.qrCode || '\u2014',
      ];
    });

    const escapeCsv = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\n');

    zip.file('attrazioni.csv', csvContent);

    // Download QR images — use gateway URL so the image encodes the correct SATI gateway link
    for (const poi of pois) {
      const gatewayUrl = getPoiGatewayUrl(poi) || await fetchGatewayUrl(poi.id);
      if (gatewayUrl) {
        try {
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(gatewayUrl)}`;
          const response = await fetch(qrUrl);
          const blob = await response.blob();
          const poiName = (poi.name as any)?.it || (poi.name as any)?.en || 'qr';
          const fileName = `${sanitizeFilename(poiName)}.png`;
          qrFolder?.file(fileName, blob);
        } catch (e) {
          console.error(`Errore nel download QR per ${(poi.name as any)?.it}:`, e);
        }
      }
    }

    // Generate and download ZIP
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attrazioni-${new Date().toISOString().split('T')[0]}.zip`;
    link.click();
    URL.revokeObjectURL(url);
  }

  if (!user || (!user.tenantId && user.role !== 'superadmin')) {
    return (
      <div className="p-6 text-mp-navy/60 text-sm">
        Questa sezione è disponibile solo per utenti associati a un tenant specifico.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-mp-navy">Gestione Attrazioni</h1>
          <p className="text-sm text-mp-navy/60 mt-0.5">Crea, modifica e gestisci i punti di interesse del tuo tenant</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportToZip}
            disabled={pois.length === 0}
            title="Esporta come ZIP con CSV + immagini QR"
            className="flex items-center gap-2 bg-gray-100 text-mp-navy/60 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download size={16} />
            Esporta
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-mp-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-mp-accent/90 transition-colors"
          >
            <Plus size={16} />
            Nuova attrazione
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-mp-navy/40">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">Caricamento attrazioni…</span>
          </div>
        ) : pois.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-mp-navy/40">
            <MapPin size={32} className="mb-2 opacity-30" />
            <p className="text-sm font-medium">Nessuna attrazione trovata</p>
            <p className="text-xs mt-1">Crea la prima attrazione con il pulsante in alto</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs text-mp-navy/50 font-medium uppercase tracking-wider">
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3">Categoria</th>
                  <th className="px-4 py-3">Coordinate</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3">Creata</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pois.map((poi) => (
                  <tr key={poi.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-mp-navy">
                        {(poi.name as any)?.it || (poi.name as any)?.en || '—'}
                      </div>
                      {((poi.name as any)?.en && (poi.name as any)?.en !== (poi.name as any)?.it) && (
                        <div className="text-xs text-mp-navy/40 mt-0.5">{(poi.name as any).en}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {poi.category?.color && (
                          <ColorDot color={poi.category.color} />
                        )}
                        <span className="text-mp-navy/80">{getCategoryName(poi.category)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-mp-navy/60 font-mono text-xs">
                      {poi.latitude.toFixed(5)}, {poi.longitude.toFixed(5)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[160px]">
                        {(poi.tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="inline-flex items-center gap-0.5 bg-gray-100 text-mp-navy/60 px-1.5 py-0.5 rounded text-xs">
                            <Tag size={9} />
                            {t}
                          </span>
                        ))}
                        {(poi.tags || []).length > 3 && (
                          <span className="text-xs text-mp-navy/40">+{poi.tags.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        poi.status === 'active'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}>
                        {poi.status === 'active' ? 'Attiva' : 'Sospesa'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-mp-navy/40 text-xs">
                      {new Date(poi.createdAt).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleStatus(poi)}
                          title={poi.status === 'active' ? 'Sospendi' : 'Attiva'}
                          className="p-1.5 rounded hover:bg-gray-100 text-mp-navy/40 hover:text-mp-navy transition-colors"
                        >
                          {poi.status === 'active' ? <PauseCircle size={16} /> : <PlayCircle size={16} />}
                        </button>
                        <button
                          onClick={() => openEdit(poi)}
                          title="Modifica"
                          className="p-1.5 rounded hover:bg-gray-100 text-mp-navy/40 hover:text-mp-navy transition-colors"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(poi)}
                          title="Elimina"
                          className="p-1.5 rounded hover:bg-red-50 text-mp-navy/40 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 !m-0">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-mp-navy">
                {editingPoi ? 'Modifica attrazione' : 'Nuova attrazione'}
              </h2>
              <button onClick={() => setModalOpen(false)} title="Chiudi" aria-label="Chiudi" className="text-mp-navy/40 hover:text-mp-navy p-1 rounded-lg hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 space-y-4 flex-1">
              {/* Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Nome (IT) *</label>
                  <input
                    type="text"
                    value={form.nameIt}
                    onChange={(e) => setForm({ ...form, nameIt: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                    placeholder="Nome in italiano"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Nome (EN)</label>
                  <input
                    type="text"
                    value={form.nameEn}
                    onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                    placeholder="Name in English"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Descrizione (IT)</label>
                  <textarea
                    value={form.descriptionIt}
                    onChange={(e) => setForm({ ...form, descriptionIt: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30 resize-none"
                    placeholder="Descrizione in italiano"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Descrizione (EN)</label>
                  <textarea
                    value={form.descriptionEn}
                    onChange={(e) => setForm({ ...form, descriptionEn: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30 resize-none"
                    placeholder="Description in English"
                  />
                </div>
              </div>

              {/* Category + Satimap */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Categoria *</label>
                  <select
                    value={form.categoryId}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm({ ...form, categoryId: value });
                      if (value !== NEW_CATEGORY_VALUE) {
                        setNewCategoryName('');
                      }
                    }}
                    aria-label="Categoria"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30 bg-white"
                  >
                    <option value="">Seleziona categoria…</option>
                    <option value={NEW_CATEGORY_VALUE}>+ Nuova categoria...</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{getCategoryName(c)}</option>
                    ))}
                  </select>
                  {(form.categoryId === NEW_CATEGORY_VALUE || (!form.categoryId && newCategoryName)) && (
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                      placeholder="Inserisci nuova categoria"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">SATIMAP *</label>
                  <select
                    value={form.satimapId}
                    onChange={(e) => setForm({ ...form, satimapId: e.target.value })}
                    aria-label="SATIMAP"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30 bg-white"
                  >
                    <option value="">{satimaps.length > 0 ? 'Seleziona mappa…' : 'Nessuna SATIMAP disponibile'}</option>
                    {satimaps.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {satimaps.length === 0 && (
                    <p className="mt-2 text-xs text-amber-700">
                      Nessuna SATIMAP disponibile per questo tenant. Crea prima una SATIMAP per poter associare l'attrazione.
                    </p>
                  )}
                </div>
              </div>

              {/* Lat / Lng */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-1">
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Latitudine *</label>
                  <input
                    type="number"
                    step="any"
                    value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                    placeholder="45.12345"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Longitudine *</label>
                  <input
                    type="number"
                    step="any"
                    value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                    placeholder="9.12345"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Altitudine (m)</label>
                  <input
                    type="number"
                    step="any"
                    value={form.altitude}
                    onChange={(e) => setForm({ ...form, altitude: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Piano</label>
                  <input
                    type="number"
                    value={form.floor}
                    onChange={(e) => setForm({ ...form, floor: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Tags + Status + QR */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Tags (separati da virgola)</label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                    placeholder="arte, contemporanea, scultura"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-mp-navy/60 mb-1">Stato</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    aria-label="Stato"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30 bg-white"
                  >
                    <option value="active">Attiva</option>
                    <option value="suspended">Sospesa</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-mp-navy/60 mb-1">Link / QR Code URL</label>
                <input
                  type="url"
                  value={form.qrCode}
                  onChange={(e) => setForm({ ...form, qrCode: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-mp-navy focus:outline-none focus:ring-2 focus:ring-mp-accent/30"
                  placeholder="https://..."
                />
              </div>

              {/* QR Code Preview */}
              {form.qrCode && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-mp-navy">Anteprima QR Code</h3>
                    <button
                      type="button"
                      onClick={downloadQRCode}
                      className="flex items-center gap-1 text-xs text-mp-accent hover:text-mp-accent/80 transition-colors"
                    >
                      <Download size={14} />
                      Scarica
                    </button>
                  </div>
                  <div className="flex justify-center p-4 bg-white rounded border border-gray-200">
                    <div ref={qrCodeRef}>
                      {/* QR encodes gateway URL if the POI already has a QRCode record,
                          otherwise shows the resource URL with a note */}
                      {editingPoi && getPoiGatewayUrl({ ...editingPoi, qrCodes: editingPoi.qrCodes }) ? (
                        <QRCode
                          value={getPoiGatewayUrl({ ...editingPoi, qrCodes: editingPoi.qrCodes })!}
                          size={200}
                          level="H"
                        />
                      ) : (
                        <QRCode
                          value={form.qrCode}
                          size={200}
                          level="H"
                        />
                      )}
                    </div>
                  </div>
                  {editingPoi && getPoiGatewayUrl({ ...editingPoi, qrCodes: editingPoi.qrCodes }) ? (
                    <div className="text-xs text-mp-navy/60 space-y-1">
                      <p>
                        <span className="font-medium text-green-700">URL Gateway (da stampare):</span>{' '}
                        <span className="font-mono break-all">
                          {getPoiGatewayUrl({ ...editingPoi, qrCodes: editingPoi.qrCodes })}
                        </span>
                      </p>
                      <p>
                        <span className="font-medium">Risorsa (destinazione):</span>{' '}
                        <span className="font-mono break-all">{form.qrCode}</span>
                      </p>
                    </div>
                  ) : (
                    <div className="text-xs text-mp-navy/60 space-y-1">
                      <p className="text-amber-600 font-medium">Gateway QR verrà generato al salvataggio</p>
                      <p>Risorsa: <span className="font-mono break-all">{form.qrCode}</span></p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm text-mp-navy/60 hover:text-mp-navy transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={
                  saving ||
                  !form.nameIt ||
                  !form.satimapId ||
                  !form.latitude ||
                  !form.longitude ||
                  (!form.categoryId && !newCategoryName.trim()) ||
                  (form.categoryId === NEW_CATEGORY_VALUE && !newCategoryName.trim())
                }
                className="flex items-center gap-2 bg-mp-accent text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-mp-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {editingPoi ? 'Salva modifiche' : 'Crea attrazione'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 !m-0">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-mp-navy">Elimina attrazione</h2>
            <p className="text-sm text-mp-navy/70">
              Sei sicuro di voler eliminare <strong>{(deleteConfirm.name as any)?.it || (deleteConfirm.name as any)?.en}</strong>?
              <br />
              <span className="text-red-600">Questa operazione è irreversibile.</span>
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-mp-navy/60 hover:text-mp-navy transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting && <Loader2 size={14} className="animate-spin" />}
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
